import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { trackInfraCumulativeLayoutShiftView } from "@faire/web-api--source/events/infra/view/cumulativeLayoutShift";
import { trackInfraFirstContentfulPaintView } from "@faire/web-api--source/events/infra/view/firstContentfulPaint";
import { trackInfraInteractionToNextPaintView } from "@faire/web-api--source/events/infra/view/interactionToNextPaint";
import { trackInfraLargestContentfulPaintView } from "@faire/web-api--source/events/infra/view/largestContentfulPaint";
import once from "lodash/once";
import { useCallback, useEffect, useRef, useState, useLayoutEffect } from "react";
// eslint-disable-next-line @faire-react/no-restricted-imports
import { onCLS, onLCP, onFCP, onINP } from "web-vitals/attribution";
import { getDocumentOrThrow } from "../../globals/getDocument";
import { HistoryTiming } from "../HistoryTiming";
var clsListeners = new Set();
var latestClsReport = null;
var lcpListeners = new Set();
var latestLcpReport = null;
var fcpListeners = new Set();
var latestFcpReport = null;
var inpListeners = new Set();
var latestInpReport = null;
// Make resetINP accessible outside startListening
var resetINP;
var resetCLS;
var startListening = once(function() {
    // This is a one-way ticket. Once you start listening, you can't stop.
    // So, no matter how many reporters we have, we want to use the same
    // listener.
    resetCLS = onCLS(function(report) {
        latestClsReport = report;
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = clsListeners[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var listener = _step.value;
                listener(report);
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    }, {
        reportAllChanges: true
    });
    onLCP(function(report) {
        latestLcpReport = report;
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = lcpListeners[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var listener = _step.value;
                listener(report);
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    }, {
        reportAllChanges: true
    });
    onFCP(function(report) {
        latestFcpReport = report;
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = fcpListeners[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var listener = _step.value;
                listener(report);
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    }, {
        reportAllChanges: true
    });
    resetINP = onINP(function(report) {
        latestInpReport = report;
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = inpListeners[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var listener = _step.value;
                listener(report);
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    }, {
        reportAllChanges: true
    });
});
/**
 * Record the Cumulative Layout Shift (CLS) metric in Chromium browsers if the associated PETE event is of type DIRECT_LOAD.
 *
 * Please note that as of 2021, Chromium changed the definition of CLS to cap the aggregation
 * to the worst "window" of shifts. See https://web.dev/articles/cls-web-tooling. This prevents
 * it from being used to track layout shifts across soft navigations.
 *
 * This means that CLS, as implemented in Chromium, cannot be used to track layout shifts
 * for soft (client-side) navigation as of May 2024, however the Chromium team is working on
 * a solution. See https://developer.chrome.com/docs/web-platform/soft-navigations-experiment
 *
 * CLS increases over time, so we wait as late as possible to report the value. Specifically,
 * we report it when the visibility state is changed to hidden (which can be when the tab
 * is hidden or closed), or when the component unmounts. CLS can be reported multiple times
 * for a single page view.
 *
 * CLS is only supported in Chromium browsers. No events will be triggered in browsers where CLS is not supported.
 *
 * If `isAction` is true, this is not a direct load and nothing will be reported.
 */ export var useTrackCumulativeLayoutShift = function(name, loadType) {
    var _getWindow_PerformanceObserver, _getWindow;
    var _useState = _sliced_to_array(useState(latestClsReport), 2), cls = _useState[0], setCls = _useState[1];
    var latestCls = useRef(cls);
    latestCls.current = cls;
    var _getWindow_PerformanceObserver_supportedEntryTypes_includes;
    var supportsCLS = (_getWindow_PerformanceObserver_supportedEntryTypes_includes = (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow_PerformanceObserver = _getWindow.PerformanceObserver) === null || _getWindow_PerformanceObserver === void 0 ? void 0 : _getWindow_PerformanceObserver.supportedEntryTypes.includes("layout-shift")) !== null && _getWindow_PerformanceObserver_supportedEntryTypes_includes !== void 0 ? _getWindow_PerformanceObserver_supportedEntryTypes_includes : false;
    useEffect(function() {
        clsListeners.add(setCls);
        startListening();
        return function() {
            clsListeners.delete(setCls);
        };
    }, [
        setCls
    ]);
    var reportCLS = useCallback(function() {
        var _latestCls_current;
        if (loadType && supportsCLS && ((_latestCls_current = latestCls.current) === null || _latestCls_current === void 0 ? void 0 : _latestCls_current.value) !== undefined && latestCls.current.attribution.loadState === "complete") {
            var _latestCls_current1, _latestCls_current2, _latestCls_current3, _latestCls_current4;
            trackInfraCumulativeLayoutShiftView(name, loadType, String(latestCls.current.value), JSON.stringify({
                largestShiftTarget: String((_latestCls_current1 = latestCls.current) === null || _latestCls_current1 === void 0 ? void 0 : _latestCls_current1.attribution.largestShiftTarget),
                largestShiftTime: Number((_latestCls_current2 = latestCls.current) === null || _latestCls_current2 === void 0 ? void 0 : _latestCls_current2.attribution.largestShiftTime),
                largestShiftValue: Number((_latestCls_current3 = latestCls.current) === null || _latestCls_current3 === void 0 ? void 0 : _latestCls_current3.attribution.largestShiftValue),
                loadState: (_latestCls_current4 = latestCls.current) === null || _latestCls_current4 === void 0 ? void 0 : _latestCls_current4.attribution.loadState
            }));
        }
    }, [
        supportsCLS,
        loadType,
        name
    ]);
    useLayoutEffect(function() {
        resetCLS === null || resetCLS === void 0 ? void 0 : resetCLS();
    }, []);
    var handleVisibilityChange = useCallback(function() {
        if (getDocumentOrThrow().visibilityState === "hidden") {
            reportCLS();
        }
    }, [
        reportCLS
    ]);
    // Report CLS when visibility changes (including on close), and when the component unmounts.
    useEffect(function() {
        addEventListener("visibilitychange", handleVisibilityChange);
        return function() {
            removeEventListener("visibilitychange", handleVisibilityChange);
            reportCLS();
        };
    }, [
        reportCLS,
        handleVisibilityChange
    ]);
    return cls;
};
export var useTrackFirstContentfulPaint = function(name, isAction) {
    var _useState = _sliced_to_array(useState(latestFcpReport), 2), fcp = _useState[0], setFcp = _useState[1];
    var latestFcp = useRef(fcp);
    latestFcp.current = fcp;
    // Whether this event will be recorded as a DIRECT_LOAD.
    var isDirectLoad = useRef(HistoryTiming.get().isInitialLoad).current && !isAction;
    var reportFcp = useCallback(function(fcp) {
        if (isDirectLoad && (fcp === null || fcp === void 0 ? void 0 : fcp.attribution.loadState) === "complete") {
            var _fcp_value;
            trackInfraFirstContentfulPaintView(name, // This is provided for consistency. FCP only applies to direct loads.
            "DIRECT_LOAD", String((_fcp_value = fcp === null || fcp === void 0 ? void 0 : fcp.value) !== null && _fcp_value !== void 0 ? _fcp_value : 0), JSON.stringify({
                firstByteToFCP: String(fcp === null || fcp === void 0 ? void 0 : fcp.attribution.firstByteToFCP),
                loadState: Number(fcp === null || fcp === void 0 ? void 0 : fcp.attribution.loadState),
                timeToFirstByte: Number(fcp === null || fcp === void 0 ? void 0 : fcp.attribution.timeToFirstByte)
            }));
        }
    }, [
        isDirectLoad,
        name
    ]);
    useEffect(function() {
        fcpListeners.add(setFcp);
        fcpListeners.add(reportFcp);
        if (latestFcp.current) {
            reportFcp(latestFcp.current);
        }
        startListening();
        return function() {
            fcpListeners.delete(setFcp);
            fcpListeners.delete(reportFcp);
        };
    }, [
        reportFcp,
        setFcp
    ]);
    return fcp;
};
export var useTrackLargestContentfulPaint = function(name, isAction) {
    var _getWindow_PerformanceObserver, _getWindow;
    var _useState = _sliced_to_array(useState(latestLcpReport), 2), lcp = _useState[0], setLcp = _useState[1];
    var latestLcp = useRef(lcp);
    latestLcp.current = lcp;
    // Whether this event will be recorded as a DIRECT_LOAD.
    var isDirectLoad = useRef(HistoryTiming.get().isInitialLoad).current && !isAction;
    var _getWindow_PerformanceObserver_supportedEntryTypes_includes;
    var supportsLCP = (_getWindow_PerformanceObserver_supportedEntryTypes_includes = (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow_PerformanceObserver = _getWindow.PerformanceObserver) === null || _getWindow_PerformanceObserver === void 0 ? void 0 : _getWindow_PerformanceObserver.supportedEntryTypes.includes("largest-contentful-paint")) !== null && _getWindow_PerformanceObserver_supportedEntryTypes_includes !== void 0 ? _getWindow_PerformanceObserver_supportedEntryTypes_includes : false;
    useEffect(function() {
        lcpListeners.add(setLcp);
        startListening();
        return function() {
            lcpListeners.delete(setLcp);
        };
    }, [
        setLcp
    ]);
    var reportLCP = useCallback(function() {
        if (supportsLCP && isDirectLoad) {
            var _latestLcp_current, _latestLcp_current1, _latestLcp_current2, _latestLcp_current3;
            var _latestLcp_current_value;
            trackInfraLargestContentfulPaintView(name, // This is provided for consistency. LCP only applies to direct loads.
            "DIRECT_LOAD", String((_latestLcp_current_value = (_latestLcp_current = latestLcp.current) === null || _latestLcp_current === void 0 ? void 0 : _latestLcp_current.value) !== null && _latestLcp_current_value !== void 0 ? _latestLcp_current_value : 0), JSON.stringify({
                element: String((_latestLcp_current1 = latestLcp.current) === null || _latestLcp_current1 === void 0 ? void 0 : _latestLcp_current1.attribution.element),
                resourceLoadTime: Number((_latestLcp_current2 = latestLcp.current) === null || _latestLcp_current2 === void 0 ? void 0 : _latestLcp_current2.attribution.resourceLoadDuration),
                elementRenderDelay: Number((_latestLcp_current3 = latestLcp.current) === null || _latestLcp_current3 === void 0 ? void 0 : _latestLcp_current3.attribution.elementRenderDelay)
            }));
        }
    }, [
        supportsLCP,
        isDirectLoad,
        name
    ]);
    var handleVisibilityChange = useCallback(function() {
        if (getDocumentOrThrow().visibilityState === "hidden") {
            reportLCP();
        }
    }, [
        reportLCP
    ]);
    // Report LCP when visibility changes (including on close), and when the component unmounts.
    useEffect(function() {
        addEventListener("visibilitychange", handleVisibilityChange);
        return function() {
            removeEventListener("visibilitychange", handleVisibilityChange);
            reportLCP();
        };
    }, [
        reportLCP,
        handleVisibilityChange
    ]);
    return lcp;
};
export var useTrackInteractionToNextPaint = function(name, loadType) {
    var _useState = _sliced_to_array(useState(latestInpReport), 2), inp = _useState[0], setInp = _useState[1];
    var latestInp = useRef(inp);
    latestInp.current = inp;
    useEffect(function() {
        inpListeners.add(setInp);
        startListening();
        return function() {
            inpListeners.delete(setInp);
        };
    }, [
        setInp
    ]);
    var reportINP = useCallback(function() {
        var _latestInp_current;
        if (loadType && ((_latestInp_current = latestInp.current) === null || _latestInp_current === void 0 ? void 0 : _latestInp_current.value) !== undefined && latestInp.current.value > 0) {
            var _latestInp_current1, _latestInp_current2, _latestInp_current3, _latestInp_current4, _latestInp_current5, _latestInp_current6;
            trackInfraInteractionToNextPaintView(name, loadType, String(latestInp.current.value), JSON.stringify({
                element: String(getElementDebugInfo((_latestInp_current1 = latestInp.current) === null || _latestInp_current1 === void 0 ? void 0 : _latestInp_current1.attribution.interactionTargetElement)),
                processingDuration: Number((_latestInp_current2 = latestInp.current) === null || _latestInp_current2 === void 0 ? void 0 : _latestInp_current2.attribution.processingDuration),
                presentationDelay: Number((_latestInp_current3 = latestInp.current) === null || _latestInp_current3 === void 0 ? void 0 : _latestInp_current3.attribution.presentationDelay),
                nextPaintTime: Number((_latestInp_current4 = latestInp.current) === null || _latestInp_current4 === void 0 ? void 0 : _latestInp_current4.attribution.nextPaintTime),
                interactionTime: Number((_latestInp_current5 = latestInp.current) === null || _latestInp_current5 === void 0 ? void 0 : _latestInp_current5.attribution.interactionTime),
                interactionType: Number((_latestInp_current6 = latestInp.current) === null || _latestInp_current6 === void 0 ? void 0 : _latestInp_current6.attribution.interactionType)
            }));
        }
    }, [
        name,
        loadType
    ]);
    useLayoutEffect(function() {
        resetINP === null || resetINP === void 0 ? void 0 : resetINP();
    }, []);
    var handleVisibilityChange = useCallback(function() {
        if (getDocumentOrThrow().visibilityState === "hidden") {
            reportINP();
        }
    }, [
        reportINP
    ]);
    // Report INP when visibility changes (including on close), and when the component unmounts.
    useEffect(function() {
        addEventListener("visibilitychange", handleVisibilityChange);
        return function() {
            removeEventListener("visibilitychange", handleVisibilityChange);
            reportINP();
        };
    }, [
        reportINP,
        handleVisibilityChange
    ]);
    return inp;
};
/**
 * Convert an HTML Node from the Web Vitals library attribution to a string for logging.
 *
 * @param node - The HTML Node to convert to a string.
 * @returns A string representation of the HTML Node.
 */ function getElementDebugInfo(node) {
    var _element_textContent;
    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
        return "";
    }
    var element = node;
    var _element_attributes;
    var attributes = Array.from((_element_attributes = element.attributes) !== null && _element_attributes !== void 0 ? _element_attributes : []).map(function(attr) {
        return "".concat(attr.name, '="').concat(attr.value, '"');
    }).join(" ");
    var id = element.id ? "#".concat(element.id) : "";
    //The className property on an SVG element has a different definition from the className property of an HTML element.
    //In HTML, className is a string. so we need to check if it's a string before calling split and join
    var classes = element.className && typeof element.className === "string" ? ".".concat(element.className.split(" ").join(".")) : "";
    var selector = "".concat(element.tagName.toLowerCase()).concat(id).concat(classes);
    return JSON.stringify({
        tagName: element.tagName,
        selector: selector,
        attributes: attributes || undefined,
        textContent: ((_element_textContent = element.textContent) === null || _element_textContent === void 0 ? void 0 : _element_textContent.trim()) || undefined,
        dimensions: {
            width: element.clientWidth,
            height: element.clientHeight
        }
    });
}
